// Local-first device storage for the Vault (TASK-VAULT-CLOUD-001).
//
// On native (Android/iOS) the encrypted file is written to the app's private
// Data directory so it's available offline and reads don't need a Drive round-
// trip. Drive remains the backup; if the local copy is missing (new device,
// cleared storage) VaultContext falls back to downloading from Drive and
// re-caches here. On web these are no-ops (no device filesystem) — web reads
// always go to Drive.

import { isNativeRuntime } from '@/lib/native/platform';

const DIR = 'vault';

function pathFor(docId: string): string {
  return `${DIR}/${docId}.enc`;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function base64ToBlob(b64: string): Blob {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes]);
}

/** Persist the encrypted blob on the device (native only; no-op on web). */
export async function writeLocal(docId: string, cipher: Blob): Promise<void> {
  if (!isNativeRuntime()) return;
  try {
    const { Filesystem, Directory } = await import('@capacitor/filesystem');
    const base64 = await blobToBase64(cipher);
    await Filesystem.writeFile({ path: pathFor(docId), data: base64, directory: Directory.Data, recursive: true });
  } catch {
    // Local cache is best-effort; Drive is the source of truth.
  }
}

/** Read the encrypted blob from the device, or null if absent (native only). */
export async function readLocal(docId: string): Promise<Blob | null> {
  if (!isNativeRuntime()) return null;
  try {
    const { Filesystem, Directory } = await import('@capacitor/filesystem');
    const { data } = await Filesystem.readFile({ path: pathFor(docId), directory: Directory.Data });
    if (typeof data === 'string') return base64ToBlob(data);
    return data as Blob;
  } catch {
    return null; // not cached locally
  }
}

/** Remove the device copy (native only; no-op on web). */
export async function removeLocal(docId: string): Promise<void> {
  if (!isNativeRuntime()) return;
  try {
    const { Filesystem, Directory } = await import('@capacitor/filesystem');
    await Filesystem.deleteFile({ path: pathFor(docId), directory: Directory.Data });
  } catch {
    // already gone
  }
}

/** Remove every cached encrypted file (native only; no-op on web). */
export async function clearLocalVault(): Promise<void> {
  if (!isNativeRuntime()) return;
  try {
    const { Filesystem, Directory } = await import('@capacitor/filesystem');
    await Filesystem.rmdir({ path: DIR, directory: Directory.Data, recursive: true });
  } catch {
    // nothing cached yet
  }
}
